// PURPOSE: Paste a Wispr transcript, share link, and initial-call points onto a meeting.
// CONTRACT: The form is always on the meeting. Save is a normal record update, so the server allows or refuses it. Unknown call-point keys already stored are kept.
// DEPENDS: Session API and the meeting record.
// CEILING: No Wispr login and no send. An assistant writes the same fields through the ERP update tool.
import { useEffect, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from './auth';
import { ErrorNotice } from './components';
import type { JsonValue, NativeRecord, RecordData } from './types';

export const CALL_POINT_KEYS = ['tools', 'pain', 'next_step'] as const;
export const CALL_POINT_LABELS: Record<(typeof CALL_POINT_KEYS)[number], string> = {
  tools: 'Tools', pain: 'Pain', next_step: 'Next step',
};

function pointsOf(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export function MeetingWispr({ meeting }: { meeting: NativeRecord }) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const canSave = meeting.allowed_actions.includes('update');
  const [transcript, setTranscript] = useState('');
  const [link, setLink] = useState('');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();

  useEffect(() => {
    const points = pointsOf(meeting.data.call_points);
    setTranscript(String(meeting.data.transcript || ''));
    setLink(String(meeting.data.wispr_share_url || ''));
    setDraft(Object.fromEntries(CALL_POINT_KEYS.map(key => [key, typeof points[key] === 'string' ? points[key] : ''])));
  }, [meeting.id, meeting.version]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!canSave || busy) return;
    const call_points: RecordData = { ...pointsOf(meeting.data.call_points) } as RecordData;
    for (const key of CALL_POINT_KEYS) {
      const value = (draft[key] || '').trim();
      if (value) call_points[key] = value;
      else delete call_points[key];
    }
    setBusy(true);
    setError(undefined);
    try {
      await api.update(meeting, { transcript, wispr_share_url: link.trim(), call_points: call_points as unknown as JsonValue });
      await queryClient.invalidateQueries();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return <form className="catalog-form" aria-label="Wispr transcript" onSubmit={event => void save(event)}>
    <h3>Wispr Flow</h3>
    <details className="form-field full"><summary>Paste transcript instead</summary><label className="form-field">Transcript<textarea aria-label="Transcript" rows={8} maxLength={200000} value={transcript} onChange={event => setTranscript(event.target.value)} /></label></details>
    <label className="form-field">Wispr Flow link<input aria-label="Wispr Flow link" value={link} onChange={event => setLink(event.target.value)} placeholder="https://notes.wisprflow.ai/shared/…" /></label>
    <h3>Initial call</h3>
    {CALL_POINT_KEYS.map(key => <label key={key} className="form-field">{CALL_POINT_LABELS[key]}<input aria-label={CALL_POINT_LABELS[key]} value={draft[key] || ''} onChange={event => setDraft(current => ({ ...current, [key]: event.target.value }))} /></label>)}
    {!!error && <ErrorNotice error={error} />}
    <div className="form-footer"><button className="button primary" disabled={!canSave || busy}>{busy ? 'Saving…' : 'Save transcript'}</button></div>
  </form>;
}
